/**
 * RefundModal — admin-only in-app refund panel for a completed ride (#9).
 *
 * Shows the original paid amount, total already refunded, and the remaining
 * refundable balance, plus the full refund history. The admin can issue a
 * full or partial refund with a mandatory reason. Multiple partials may stack
 * up to the amount paid (enforced server-side).
 *
 * Refunds are recorded as separate financial events — the ride record is never
 * mutated, and nothing here touches dispatch / lifecycle.
 */
import React, { useEffect, useState } from "react";
import { X, Loader2, RotateCcw, CreditCard, Banknote, Briefcase } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const METHOD_META = {
  card: { icon: CreditCard, label: "Card", note: "Refunds the original card via Stripe." },
  cash: { icon: Banknote, label: "Cash", note: "Audit record only — hand the cash back manually." },
  account: { icon: Briefcase, label: "Account", note: "Credits the corporate account ledger." },
};

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

// Refunds Phase 2 — canonical reason presets (mirrors the backend list).
const REASON_PRESETS = [
  "Driver took a wrong/longer route",
  "Service issue / complaint",
  "Duplicate or incorrect charge",
  "Overcharged fare",
  "Customer goodwill",
  "Booking cancelled after charge",
  "Other",
];
const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).replace(",", "");
};

export const RefundModal = ({ ride, onClose, onRefunded }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [reasonPreset, setReasonPreset] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/rides/${ride.id}/refunds`);
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride.id]);

  const remaining = summary?.remaining_refundable ?? 0;
  const meta = METHOD_META[summary?.payment_method] || METHOD_META.cash;
  const MethodIcon = meta.icon;

  const submit = async () => {
    const reason = reasonPreset === "Other" ? reasonText.trim() : reasonPreset;
    if (!reasonPreset) {
      toast.error("Please choose a refund reason");
      return;
    }
    if (reasonPreset === "Other" && reason.length < 2) {
      toast.error("Please describe the reason");
      return;
    }
    const amtNum = amount === "" ? null : Number(amount);
    if (amtNum !== null && (isNaN(amtNum) || amtNum <= 0)) {
      toast.error("Enter a valid amount, or leave blank to refund the full remaining balance");
      return;
    }
    if (amtNum !== null && amtNum - remaining > 0.0049) {
      toast.error(`Amount can't exceed the remaining refundable ${money(remaining)}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/rides/${ride.id}/refund`, {
        ...(amtNum !== null ? { amount: amtNum } : {}),
        reason,
      });
      setSummary(data.summary);
      setAmount("");
      setReasonPreset("");
      setReasonText("");
      toast.success(`Refunded ${money(data.refund.amount)}`);
      onRefunded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="refund-modal" onMouseDown={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Refund ride
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{ride.passenger_name} · {ride.id.substring(0, 8)}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="refund-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground" data-testid="refund-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : summary ? (
          <div className="p-5 space-y-4">
            {/* Method + amounts */}
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-secondary border border-border font-semibold" data-testid="refund-method">
                <MethodIcon className="w-3.5 h-3.5" /> {meta.label}
              </span>
              <span className="text-xs text-muted-foreground">{meta.note}</span>
            </div>

            <div className="grid grid-cols-3 gap-px bg-border border border-border" data-testid="refund-amounts">
              <div className="bg-white p-3 text-center">
                <div className="label-mini text-muted-foreground">ORIGINAL</div>
                <div className="font-heading font-black text-lg mt-0.5" data-testid="refund-original">{money(summary.original_amount)}</div>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="label-mini text-muted-foreground">REFUNDED</div>
                <div className="font-heading font-black text-lg mt-0.5 text-[#EF4444]" data-testid="refund-total">{money(summary.total_refunded)}</div>
              </div>
              <div className="bg-white p-3 text-center">
                <div className="label-mini text-muted-foreground">REMAINING</div>
                <div className="font-heading font-black text-lg mt-0.5 text-[#16A34A]" data-testid="refund-remaining">{money(summary.remaining_refundable)}</div>
              </div>
            </div>

            {summary.card_unpaid && (
              <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FFC107] p-2" data-testid="refund-card-unpaid">
                This card ride hasn't been paid yet — there's nothing to refund.
              </div>
            )}

            {/* Refund form */}
            {summary.refundable ? (
              <div className="space-y-3 border-t border-border pt-4">
                <div>
                  <label className="label-mini text-muted-foreground">Amount (blank = full remaining {money(remaining)})</label>
                  <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
                    <span className="text-muted-foreground mr-1.5">£</span>
                    <input
                      type="number" step="0.01" min="0" max={remaining}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={remaining.toFixed(2)}
                      className="flex-1 outline-none bg-transparent text-sm"
                      data-testid="refund-amount-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="label-mini text-muted-foreground">Reason (required)</label>
                  <select
                    value={reasonPreset}
                    onChange={(e) => setReasonPreset(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-border bg-white text-sm outline-none"
                    data-testid="refund-reason-select"
                  >
                    <option value="">Select a reason…</option>
                    {REASON_PRESETS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {reasonPreset === "Other" && (
                    <textarea
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Describe the reason"
                      rows={2}
                      maxLength={500}
                      className="w-full mt-2 px-3 py-2 border border-border bg-white text-sm outline-none resize-none"
                      data-testid="refund-reason-input"
                    />
                  )}
                </div>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white disabled:opacity-50"
                  data-testid="refund-submit-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Processing…</> : "Issue refund"}
                </Button>
              </div>
            ) : (
              !summary.card_unpaid && (
                <div className="text-sm text-muted-foreground border-t border-border pt-4" data-testid="refund-fully-refunded">
                  This ride is fully refunded — no balance remaining.
                </div>
              )
            )}

            {/* History */}
            <div className="border-t border-border pt-4">
              <div className="label-mini text-muted-foreground mb-2">REFUND HISTORY</div>
              {summary.refunds?.length ? (
                <div className="space-y-1.5" data-testid="refund-history">
                  {summary.refunds.map((r) => (
                    <div key={r.id} className="flex items-start justify-between gap-3 text-xs border border-border p-2" data-testid={`refund-history-${r.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold">{money(r.amount)} · <span className="uppercase text-muted-foreground">{r.kind}</span> · <span className={r.status === "succeeded" ? "text-[#16A34A]" : "text-[#EF4444]"}>{r.status}</span></div>
                        {r.reason && <div className="text-muted-foreground truncate">{r.reason}</div>}
                        <div className="text-muted-foreground">{fmtTs(r.created_at)} · {r.admin_name || "admin"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground" data-testid="refund-history-empty">No refunds yet.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-10 text-center text-muted-foreground">Couldn't load refund details.</div>
        )}
      </div>
    </div>
  );
};

export default RefundModal;
